import React, { memo } from "react";

import Highlighter from "react-highlight-words";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import { isCustomSpecProduct } from "./Helper";
import ProductSpecTypeBadge from "./ProductSpecTypeBadge";

import { getProductDisplayData } from "../../utils";

import { ReactComponent as AlertFillIcon } from "../../../../../assets/images/product/specmanager/alert-fill.svg";
import { ReactComponent as CloseFillIcon } from "../../../../../assets/images/product/specmanager/close-fill.svg";
import "./ProductList.css";
import "./SpecDisplayTable.css";

const TOOLTIP_STANDARD_SPEC = "This product follows the standard spec for this test.";
const TOOLTIP_CUSTOM_SPEC = "This product follows the custom spec defined for the specific product.";
const TOOLTIP_NON_REMOVABLE = "This product is currently under testing or has been tested and cannot be removed.";

const specManagerTooltipContainer = (trigger) => trigger?.closest(".ant-drawer-body") || trigger?.closest(".ant-modal-body") || document.body;

function renderHighlightedValue(searchTerm, value) {
  const text = value != null ? String(value) : "";
  const term = searchTerm && String(searchTerm).trim();
  if (!term) return text;
  return (
    <Highlighter
      highlightClassName="searchHighlight"
      searchWords={[term]}
      autoEscape
      textToHighlight={text}
    />
  );
}

function ProductListItem({
  product,
  fields,
  linkReportJsonFields,
  searchTerm,
  loading,
  onRemoveProduct,
  tooltipPlacement = "left",
}) {
  const { titleToValue, fullText } = getProductDisplayData(product, fields, linkReportJsonFields);
  const entries = Object.entries(titleToValue || {});
  const identifier = product.product_identifier || {};
  const fallbackLabel = product.product_name || identifier.item || identifier.description || "";

  const isNonRemovable = product.test_submitted === true;
  const isCustom = isCustomSpecProduct(product);

  const rowHoverClass = !isNonRemovable && !loading ? " ProductList__SelectedListItem--rowHover" : "";

  return (
    <li className={`ProductList__SelectedListItem${rowHoverClass}`}>
      <StyledTooltip
        placement={tooltipPlacement}
        title={fullText}
        className="SpecManagerProductTooltip"
        getPopupContainer={specManagerTooltipContainer}
      >
        <span className="ProductList__SelectedRowTooltipTrigger">
          <div className="ProductList__SelectedItemLeft">
            <div className="ProductList__SelectedItemText">
              {entries.length > 0
                ? entries.map(([k, v], i) => (
                  <React.Fragment key={k}>
                    {i > 0 && " \u2022 "}
                    <strong>
                      {k}
                      :
                    </strong>
                    {" "}
                    {renderHighlightedValue(searchTerm, v != null ? v : "")}
                  </React.Fragment>
                ))
                : renderHighlightedValue(searchTerm, fallbackLabel)}
            </div>
          </div>
        </span>
      </StyledTooltip>

      <div className={`ProductList__SelectedItemRight ${isNonRemovable ? "ProductList__SelectedItemRight--nonremovable" : ""}`}>
        <StyledTooltip
          placement={tooltipPlacement}
          title={isCustom ? TOOLTIP_CUSTOM_SPEC : TOOLTIP_STANDARD_SPEC}
          className="SpecManagerProductTooltip"
          getPopupContainer={specManagerTooltipContainer}
        >
          <span className="ProductList__SpecTypeBadgeTooltipTrigger">
            <ProductSpecTypeBadge
              variant={isCustom ? "custom" : "standard"}
              customSpecs={isCustom ? product.custom_specs : undefined}
              appearance="productList"
              suppressNativeTitle
            />
          </span>
        </StyledTooltip>

        {isNonRemovable ? (
          <StyledTooltip
            placement={tooltipPlacement}
            title={TOOLTIP_NON_REMOVABLE}
            className="SpecManagerProductTooltip"
            getPopupContainer={specManagerTooltipContainer}
          >
            <div
              className="ProductList__SelectedItemAlertIcon"
              role="img"
              aria-label={TOOLTIP_NON_REMOVABLE}
            >
              <AlertFillIcon width={20} height={20} aria-hidden />
            </div>
          </StyledTooltip>
        ) : (
          <div
            className={`ProductList__SelectedItemCloseIcon${loading ? " ProductList__SelectedItemCloseIcon--disabled" : ""}`}
            onClick={loading ? null : () => onRemoveProduct?.(product)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading && onRemoveProduct) {
                onRemoveProduct(product);
              }
            }}
            role="button"
            tabIndex={loading ? -1 : 0}
            aria-label="Remove product"
            aria-disabled={loading || undefined}
          >
            <CloseFillIcon width={8} height={8} />
          </div>
        )}
      </div>
    </li>
  );
}

const MemoizedProductListItem = memo(ProductListItem);

/**
 * Displays selected products with remove option.
 * Uses dynamic linked fields when available.
 * Virtualizes list when item count exceeds threshold for large lists.
 */
function ProductList(props) {
  const {
    selectedProducts,
    totalSelectedCount,
    searchTerm = "",
    loading,
    onRemoveProduct,
    fields = [],
    linkReportJsonFields = [],
    tooltipPlacement = "left",
  } = props;

  const isEmpty = !loading && (!selectedProducts || selectedProducts.length === 0);
  const searchActive = Boolean(searchTerm && String(searchTerm).trim());
  const totalSelected = totalSelectedCount != null
    ? totalSelectedCount
    : (selectedProducts?.length ?? 0);

  const emptyStateContent = (
    <div className="ProductList__EmptyState">
      <h4 className="ProductList__EmptyStateTitle">
        {searchActive && totalSelected > 0
          ? "No selected products match this search"
          : "Please select a product"}
      </h4>
      <h2 className="ProductList__EmptyStateSubtitle">
        Check the All Products list to add products.
      </h2>
    </div>
  );

  const listContent = (
    <ul className="ProductList__SelectedList">
      {selectedProducts.map((product) => (
        <MemoizedProductListItem
          key={product.product_id}
          product={product}
          fields={fields}
          linkReportJsonFields={linkReportJsonFields}
          searchTerm={searchTerm}
          loading={loading}
          onRemoveProduct={onRemoveProduct}
          tooltipPlacement={tooltipPlacement}
        />
      ))}
    </ul>
  );

  return (
    <StyledScrollbar id="ProductList__ScrollBar" className="ProductList__SelectedListScrollbar">
      {isEmpty ? emptyStateContent : listContent}
    </StyledScrollbar>
  );
}

export default memo(ProductList);
